import { Controller } from 'stimulus'

// Job template picker on a survey's Sending tab: chosen templates as chips,
// a search box listing the rest (with completed jobs in the last 12 months),
// and hidden job_template_ids[] fields for the form. Dimmed while "Every
// completed job" is picked.
export default class extends Controller {
  static targets = ['wrap', 'picker', 'chips', 'input', 'drop', 'field', 'fields', 'hint'];

  connect() {
    this.options = JSON.parse(this.data.get('options'))
    this.selected = new Set(this.fieldTargets.map(f => Number(f.value)))
    this.fieldTargets.forEach(f => f.remove())
    this.active = -1
    this.outside = (event) => { if (!this.pickerTarget.contains(event.target)) this.close() }
    document.addEventListener('mousedown', this.outside)
    this.render()
    this.scopeChanged()
  }

  disconnect() {
    document.removeEventListener('mousedown', this.outside)
  }

  scopeChanged() {
    const all = this.element.querySelector('input[name="survey_template[job_scope]"][value="all"]')
    const off = all && all.checked
    this.wrapTarget.style.opacity = off ? 0.45 : 1
    this.wrapTarget.style.pointerEvents = off ? 'none' : ''
    this.count()
  }

  search() {
    this.active = -1
    this.open()
  }

  open() {
    const term = this.inputTarget.value.trim().toLowerCase()
    const matches = this.options.filter(o => !this.selected.has(o.id) && o.name.toLowerCase().includes(term)).slice(0, 60)
    this.matches = matches
    this.dropTarget.innerHTML = matches.length
      ? matches.map((o, i) => `<button type="button" data-id="${o.id}" class="${i === this.active ? 'is-active' : ''}">${this.escape(o.name)}${o.inactive ? ' (inactive)' : ''}<span>${o.jobs} jobs</span></button>`).join('')
      : '<div class="sv-hint" style="padding:8px 10px;margin:0;">No other job templates match.</div>'
    this.dropTarget.hidden = false
    this.dropTarget.querySelectorAll('button[data-id]').forEach((b) => {
      b.addEventListener('mousedown', (event) => {
        event.preventDefault()
        this.add(Number(b.dataset.id))
      })
    })
  }

  close() {
    this.dropTarget.hidden = true
  }

  key(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const max = (this.matches || []).length - 1
      this.active = Math.max(0, Math.min(max, this.active + (event.key === 'ArrowDown' ? 1 : -1)))
      this.open()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const pick = (this.matches || [])[this.active >= 0 ? this.active : 0]
      if (pick) this.add(pick.id)
    } else if (event.key === 'Escape') {
      this.close()
    } else if (event.key === 'Backspace' && !this.inputTarget.value && this.selected.size) {
      const last = Array.from(this.selected).pop()
      this.remove(last)
    }
  }

  add(id) {
    this.selected.add(id)
    this.inputTarget.value = ''
    this.active = -1
    this.render()
    this.open()
  }

  remove(id) {
    this.selected.delete(id)
    this.render()
  }

  render() {
    const byId = new Map(this.options.map(o => [o.id, o]))
    this.chipsTarget.innerHTML = Array.from(this.selected).filter(id => byId.has(id)).map(id => (
      `<span class="sv-pchip">${this.escape(byId.get(id).name)}<button type="button" data-remove="${id}" aria-label="Remove">&times;</button></span>`
    )).join('')
    this.chipsTarget.querySelectorAll('[data-remove]').forEach((b) => {
      b.addEventListener('click', () => this.remove(Number(b.dataset.remove)))
    })
    this.fieldsTarget.innerHTML = Array.from(this.selected).map(id => `<input type="hidden" name="job_template_ids[]" value="${id}">`).join('')
    this.count()
  }

  count() {
    if (!this.hasHintTarget) return
    const all = this.element.querySelector('input[name="survey_template[job_scope]"][value="all"]')
    const without = this.element.querySelector('input[type=checkbox][name="survey_template[include_jobs_without_template]"]')
    const byId = new Map(this.options.map(o => [o.id, o]))
    let jobs = Array.from(this.selected).reduce((sum, id) => sum + ((byId.get(id) || {}).jobs || 0), 0)
    if (without && without.checked) jobs += Number(this.data.get('without') || 0)
    this.hintTarget.textContent = all && all.checked ? '' : `About ${Math.round(jobs / 12)} completed jobs a month, from the last 12 months.`
  }

  escape(text) {
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
